<script setup lang="ts">
import { cva } from 'class-variance-authority';
import { computed, type HTMLAttributes } from 'vue';
import { cn } from '@/lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-display font-medium transition-colors disabled:pointer-events-none disabled:opacity-45 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-gold text-ink hover:bg-gold-bright active:bg-gold-dim',
        secondary: 'bg-ink-raised text-parchment border border-ink-line hover:border-gold-dim',
        ghost: 'text-parchment hover:bg-ink-raised',
        outline: 'border border-gold-dim text-gold hover:bg-gold/10',
        danger: 'border border-red-500/40 text-red-300 hover:bg-red-500/10',
      },
      size: {
        sm: 'h-9 px-3 text-sm [&_svg]:size-4',
        md: 'h-11 px-5 text-base [&_svg]:size-5',
        lg: 'h-13 px-6 text-lg [&_svg]:size-5',
        icon: 'size-11 [&_svg]:size-5',
      },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', block: false },
  },
);

/**
 * Props are spelled out rather than derived from cva's VariantProps: the Vue
 * SFC compiler resolves defineProps types statically and cannot follow cva's
 * conditional types.
 */
const props = withDefaults(
  defineProps<{
    variant?: ButtonVariant;
    size?: ButtonSize;
    block?: boolean;
    as?: 'button' | 'a';
    type?: 'button' | 'submit';
    disabled?: boolean;
    class?: HTMLAttributes['class'];
  }>(),
  {
    variant: 'primary',
    size: 'md',
    block: false,
    as: 'button',
    type: 'button',
    disabled: false,
  },
);

const classes = computed(() =>
  cn(buttonVariants({ variant: props.variant, size: props.size, block: props.block }), props.class),
);
</script>

<template>
  <component
    :is="as"
    :class="classes"
    :type="as === 'button' ? type : undefined"
    :disabled="as === 'button' ? disabled : undefined"
    :aria-disabled="disabled || undefined"
  >
    <slot />
  </component>
</template>
